import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

interface DialogData {
  exception: any;
  staffName: string;
}

@Component({
    selector: 'app-exception-dialog',
    templateUrl: './exception-dialog.component.html',
    styleUrls: ['./exception-dialog.component.css'],
    standalone: false
})
export class ExceptionDialogComponent implements OnInit {
  exceptionForm: UntypedFormGroup;

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<ExceptionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.setupValidationLogic();
  }

  private buildForm(): void {
    this.exceptionForm = this.fb.group({
      date: [this.data.exception ? this.data.exception.date : '', Validators.required],
      isClosed: [this.data.exception ? this.data.exception.isClosed : false],
      startTime: [this.data.exception ? this.data.exception.startTime : ''],
      endTime: [this.data.exception ? this.data.exception.endTime : '']
    });
  }

  private setupValidationLogic(): void {
    this.exceptionForm.get('isClosed').valueChanges.subscribe(isClosed => {
      const startTimeControl = this.exceptionForm.get('startTime');
      const endTimeControl = this.exceptionForm.get('endTime');

      if (isClosed) {
        startTimeControl.clearValidators();
        endTimeControl.clearValidators();
        startTimeControl.setValue('');
        endTimeControl.setValue('');
      } else {
        startTimeControl.setValidators([Validators.required]);
        endTimeControl.setValidators([Validators.required]);
      }

      startTimeControl.updateValueAndValidity();
      endTimeControl.updateValueAndValidity();
    });
  }

  onSave(): void {
    if (this.exceptionForm.valid) {
      const formValue = this.exceptionForm.value;
      
      const transformedValue = {
        exceptionDate: this.formatDateToDateOnly(formValue.date),
        isClosed: formValue.isClosed,
        startTime: formValue.isClosed ? null : this.formatTimeToTimeSpan(formValue.startTime),
        endTime: formValue.isClosed ? null : this.formatTimeToTimeSpan(formValue.endTime)
      };
      
      this.dialogRef.close(transformedValue);
    } else {
      this.markFormGroupTouched(this.exceptionForm);
    }
  }

  private formatDateToDateOnly(value: any): string | null {
    if (!value) {
      return null;
    }
    
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
    
    return null;
  }

  private formatTimeToTimeSpan(value: any): string | null {
    if (!value) {
      return null;
    }
    
    if (typeof value === 'string') {
      const timeParts = value.split(':');
      if (timeParts.length === 2) {
        return `${value}:00`;
      }
      return value; 
    }
    
    return null;
  }

  private markFormGroupTouched(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control.markAsTouched();
    });
  }
}
